import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiSmartphone, FiClock, FiBarChart2, FiUser, FiCheckCircle, FiAlertCircle, FiKey } from 'react-icons/fi';
import AuthService from '../services/AuthService';

const Login = () => {
  const history = useHistory();
  const [step, setStep] = useState(1); // 1 = Enter Phone, 2 = Passwordless Login OTP, 3 = Passwordless Register
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  
  // Registration specific state fields
  const [name, setName] = useState('');
  const [brandName, setBrandName] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // Step 1: Check mobile number & send WhatsApp OTP
  const handleCheckPhone = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    setLoading(true);
    setError('');
    setInfoMessage('');

    try {
      const res = await AuthService.checkPartner(phone);
      if (res && res.exists) {
        setInfoMessage(`Account found! WhatsApp OTP sent to +91 ${phone}`);
        setStep(2); // Existing registered partner -> Proceed to Passwordless OTP Login
      } else {
        setStep(3); // Unregistered -> Proceed directly to Passwordless Registration
      }
    } catch (err) {
      console.error('Check partner error:', err);
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Passwordless Login for Existing Partner
  const handlePasswordlessLoginSubmit = async (e) => {
    e.preventDefault();
    if (!otp) {
      setError('Please enter the OTP sent to your WhatsApp.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await AuthService.login(undefined, undefined, phone, otp);
      history.push('/app');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || err.message || 'Login failed. Invalid OTP code.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: 100% Phone & OTP Passwordless Registration
  const handlePasswordlessRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!name || !brandName) {
      setError('Both Organization / Brand Name and Contact Name are required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const partnerData = {
        name,
        phone,
        otp,
        brandName,
      };

      await AuthService.registerPartner(partnerData);
      history.push('/app');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans flex flex-col md:flex-row">
      
      {/* ════════════════════════════════════════════════════════════
           LEFT SIDE: Benefits of using Tapcoe Do It Yourself Tool
         ════════════════════════════════════════════════════════════ */}
      <div className="w-full md:w-1/2 p-8 md:p-16 flex flex-col justify-between bg-slate-50 border-r border-slate-200">
        <div>
          {/* Top Brand Header */}
          <div className="flex items-center gap-2 mb-10">
            <img src="/assets/images/logo.png" alt="Tapcoe" className="w-8 h-8 rounded-full" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="font-extrabold text-xl tracking-tight text-purple-700">tapcoe</span>
            <span className="text-xs bg-purple-100 text-purple-800 font-bold px-2.5 py-0.5 rounded-full">Partner DIY</span>
          </div>

          {/* Headline */}
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-12">
            Benefits of using Tapcoe DIY<br />
            <span className="text-purple-600">our new event management tool</span>
          </h1>

          {/* Benefits Bullet List */}
          <div className="space-y-10">
            {/* Benefit 1 */}
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 text-2xl shrink-0 shadow-sm">
                <FiSmartphone />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900 mb-1">Quick & easy registration</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Complete your registration with just your mobile number & brand profile details.
                </p>
              </div>
            </div>

            {/* Benefit 2 */}
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600 text-2xl shrink-0 shadow-sm">
                <FiClock />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900 mb-1">Take your events live superfast!</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Publish your event within just 15 minutes! Add event details, dates and ticket tiers and BAM! Your event is ready.
                </p>
              </div>
            </div>

            {/* Benefit 3 */}
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 text-2xl shrink-0 shadow-sm">
                <FiBarChart2 />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900 mb-1">Monitor analytics & insights</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Track event sales, daily ticketing, get daily insights and check-in scans in real time.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Left Footer Info */}
        <div className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-400">
          © 2026 Tapcoe Partner Portal. All rights reserved.
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════
           RIGHT SIDE: Partner DIY Auth Panel (Passwordless & Email-less)
         ════════════════════════════════════════════════════════════ */}
      <div className="w-full md:w-1/2 p-8 md:p-16 flex flex-col justify-center items-center bg-white">
        <div className="max-w-md w-full">
          
          {/* Logo Badge */}
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-rose-600 text-white font-black px-3 py-1.5 rounded-lg text-sm tracking-wider uppercase shadow-md">
              my
            </div>
            <span className="text-2xl font-black tracking-tight text-slate-900">
              Do It Yourself
            </span>
          </div>

          {/* Feedback Alerts */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-3">
              <FiAlertCircle className="w-5 h-5 shrink-0 text-red-600" />
              <span>{error}</span>
            </div>
          )}

          {infoMessage && (
            <div className="mb-6 p-4 rounded-xl bg-purple-50 border border-purple-200 text-purple-800 text-xs flex items-center gap-3">
              <FiCheckCircle className="w-5 h-5 shrink-0 text-purple-600" />
              <span>{infoMessage}</span>
            </div>
          )}

          {/* STEP 1: ASK MOBILE NUMBER */}
          {step === 1 && (
            <form onSubmit={handleCheckPhone} className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2">Mobile no.</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your mobile no"
                  required
                  className="w-full bg-white border border-slate-300 rounded-xl py-3.5 px-4 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all font-medium"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-6 rounded-xl bg-slate-300 text-slate-700 font-bold text-sm transition-all shadow-sm flex items-center justify-center gap-2"
                style={{ background: phone.length >= 10 ? '#7B2FBE' : undefined, color: phone.length >= 10 ? '#fff' : undefined }}
              >
                {loading ? 'Sending WhatsApp OTP...' : 'Send OTP'}
              </button>
            </form>
          )}

          {/* STEP 2: PASSWORDLESS LOGIN FOR EXISTING PARTNER */}
          {step === 2 && (
            <form onSubmit={handlePasswordlessLoginSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Mobile Number</label>
                <input
                  type="text"
                  value={`+91 ${phone}`}
                  disabled
                  className="w-full bg-slate-100 border border-slate-200 rounded-xl py-3 px-4 text-xs font-bold text-slate-600"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">WhatsApp OTP Code *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 font-mono tracking-widest"
                  />
                  <FiKey className="absolute left-3.5 top-3.5 text-slate-400 w-4 h-4" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-6 rounded-xl bg-purple-700 hover:bg-purple-800 text-white font-bold text-xs transition-all shadow-md flex items-center justify-center gap-2"
              >
                {loading ? 'Verifying OTP...' : 'Verify OTP & Sign In'}
              </button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-center text-xs font-bold text-purple-700 hover:underline"
              >
                ← Change Mobile Number
              </button>
            </form>
          )}

          {/* STEP 3: PASSWORDLESS & EMAIL-LESS REGISTER NEW PARTNER & CREATE BRAND */}
          {step === 3 && (
            <form onSubmit={handlePasswordlessRegisterSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Organization / Brand Name *</label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. Sunburn Campus / IIT Delhi Fest"
                  required
                  className="w-full bg-white border border-slate-300 rounded-xl py-2.5 px-3.5 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Full Contact Name *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl py-2.5 pl-9 pr-3.5 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600"
                  />
                  <FiUser className="absolute left-3 top-3 text-slate-400 w-3.5 h-3.5" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">WhatsApp OTP Code *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP code sent to WhatsApp"
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl py-2.5 pl-9 pr-3.5 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 font-mono"
                  />
                  <FiKey className="absolute left-3 top-3 text-slate-400 w-3.5 h-3.5" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-6 rounded-xl bg-purple-700 hover:bg-purple-800 text-white font-bold text-xs transition-all shadow-md flex items-center justify-center gap-2 mt-2"
              >
                {loading ? 'Creating Brand Profile...' : 'Complete Profile & Create Brand'}
              </button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-center text-xs font-bold text-slate-500 hover:underline"
              >
                ← Change Mobile Number
              </button>
            </form>
          )}

          {/* Footer Assistance */}
          <div className="mt-12 text-center text-xs text-slate-400 space-y-1">
            <p>Incase of any query, please write to</p>
            <a href="mailto:support@tapcoe.com" className="font-bold text-purple-700 hover:underline">
              support@tapcoe.com
            </a>
          </div>

        </div>
      </div>

    </div>
  );
};

export default Login;
